import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  input
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { FieldControlService, IField, PsicTooltipDirective, TagComponent, AlertComponent, InputRadioComponent, InputTextComponent } from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-information-card',
    imports: [
    CommonModule,
    MatIcon,
    ReactiveFormsModule,
    PsicTooltipDirective,
    TagComponent,
    AlertComponent,
    InputRadioComponent,
    InputTextComponent
],
    standalone: true,
    providers: [FieldControlService],
    templateUrl: './information-card.component.html',
    styleUrl: './information-card.component.scss'
})
export class InformationCardComponent implements OnDestroy,AfterViewInit {

    private _field!: IField;
    @Input()
    set field(value: IField) {
        this._field = value;
        // Handle async field updates safely
        if (value?.name === 'paymentAmount') {
            this.selectedAmtToPayFields =
                value.options?.optionsList?.filter(
                    option => option.type === 'radio'
                ) || [];
        }
    }
    get field(): IField {
        return this._field;
    }
    readonly form = input<UntypedFormGroup>();
    @Output() formValid = new EventEmitter();
    isMobile = false;
    displayContent = false;
    selectedAmtToPayFields: IField[] = [];
    sink = new Subscription();

    constructor(private breakpointObserver: BreakpointObserver) {
        this.isMobile = this.breakpointObserver.isMatched('(max-width: 768px)');

        this.sink.add(
            this.breakpointObserver
                .observe(['(max-width: 768px)'])
                .subscribe(result => {
                    this.isMobile = result.matches;
                })
        );
    }

    ngAfterViewInit(): void {
        if (this.field?.name === 'paymentAmount' && this.form()) {
            this.subscribeToRadioFields();
        }
    }

    private subscribeToRadioFields(): void {
        this.selectedAmtToPayFields.forEach(field => {
            const control = this.form()!.get(field.id);
            if (control) {
                this.sink.add(
                    control.valueChanges.subscribe((value) => {
                        if (value) {
                            this.handleRadioSelection(field, value);
                        }
                        this.formValid.emit(this.form()!.valid);
                    })
                );
            }
        });
    }

    handleRadioSelection(field: IField, value: any): void {
        this.resetOtherRadioControls(field.id);
        this.handleAmountSelection(value);
        this.updatePaymentAmountType(field);
    }

    resetOtherRadioControls(selectedFieldId: string): void {
        this.selectedAmtToPayFields.forEach(otherField => {
            if (otherField.id !== selectedFieldId) {
                this.form()!.get(otherField.id)?.setValue(null, { emitEvent: false });
            }
        });
    }

    handleAmountSelection(value: any): void {
        if (value === 'OD') {
            this.enableOtherAmount();
        } else {
            this.disableOtherAmount();
            this.form()!.get('selectedAmtToPay')?.setValue(value, { emitEvent: false });
        }
    }

    updatePaymentAmountType(field: IField): void {
        const selectedOption = field.options?.optionsList?.[0];
        const form = this.form();
        if (selectedOption && form?.get('paymentAmountType')) {
            form.get('paymentAmountType')?.setValue(selectedOption.description, { emitEvent: false });
        }
    }

    ngOnDestroy(): void {
        this.sink.unsubscribe();
    }

    enableOtherAmount(): void {
        const otherField = this.field?.getOption('OtherAmountField');
        const form = this.form();
        const otherCtrl = form?.get('OtherAmountField');

        if (otherField?.disabled) {
            otherField.disabled = false;
            otherCtrl?.enable({ emitEvent: false });
            form?.get('selectedAmtToPay')?.setValue('$0.00', { emitEvent: false });
            
            // Subscribe to changes after enabling
            if (otherCtrl) {
                this.sink.add(
                    otherCtrl.valueChanges.subscribe((otherValue) => {
                        const formValue = this.form();
                        if (otherValue && !otherCtrl.errors) {
                            formValue?.get('selectedAmtToPay')?.setValue(`$${otherValue.toFixed(2)}`, { emitEvent: false });
                        } else if (otherCtrl.errors) {
                            formValue?.get('selectedAmtToPay')?.setValue('$0.00', { emitEvent: false });
                        }
                        if (formValue?.valid) {
                            this.formValid.emit(true);
                        } else {
                            this.formValid.emit(false);
                        }
                    })
                );
            }
        }
    }

    disableOtherAmount(): void {
        const otherField = this.field?.getOption('OtherAmountField');
        const otherCtrl = this.form()?.get('OtherAmountField');

        if (otherField) {
            otherField.disabled = true;
        }

        otherCtrl?.setValue('', { emitEvent: false });
        otherCtrl?.disable({ emitEvent: false });
    }

    toggleContent(): void {
        this.displayContent = !this.displayContent;
    }

    getTagTheme(status: string): string {
        switch (status?.toLowerCase()) {
            case 'inforce':
                return 'neutralAq';
            case 'new':
                return 'neutralPurple';
            case 'expired':
                return 'error';
            default:
                return 'default';
        }
    }
}
